import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

interface DeleteEntityDialogProps<T extends { id: number; name: string }> {
  entity: T | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityName: string;
  onDelete: (id: number) => Promise<void> | void;
  isDeleting?: boolean;
  customDescription?: string;
  conflictMessage?: string;
}

export function DeleteEntityDialog<T extends { id: number; name: string }>({
  entity,
  open,
  onOpenChange,
  entityName,
  onDelete,
  isDeleting = false,
  customDescription,
  conflictMessage,
}: DeleteEntityDialogProps<T>) {
  const handleDeleteConfirm = async () => {
    if (!entity) return;

    try {
      await onDelete(entity.id);
      toast.success(`${entityName} "${entity.name}" deleted successfully`);
      onOpenChange(false);
    } catch (error: any) {
      const statusCode = error?.response?.status;
      const errorDetail = error?.response?.data?.detail || "";

      if (statusCode === 409) {
        const message = conflictMessage || errorDetail || `Cannot delete ${entityName.toLowerCase()} "${entity.name}"`;
        toast.error(message);
      } else if (statusCode === 404) {
        toast.error(`${entityName} "${entity.name}" not found`);
      } else {
        toast.error(
          `Failed to delete ${entityName.toLowerCase()} "${entity.name}"${errorDetail ? `: ${errorDetail}` : ""}`
        );
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {entityName}</DialogTitle>
          <DialogDescription>
            {customDescription || `Are you sure you want to delete "${entity?.name}"?`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
